import { AbstractControl, ValidationErrors } from '@angular/forms';

// US/CA phone helpers. Deliberately narrow: moneta's billing addresses are
// US-first (tax nexus), and a full international parser is a dependency this
// screen does not need. Revisit when non-NANP countries are onboarded.

/** Keep digits only, and drop a leading US country code. */
export function phoneDigits(value: string | null | undefined): string {
  // A HALF-TYPED value formatted by PhoneNationalPipe ("(415) 5"; a pasted "+1 (415) 5" too) comes back through the
  // input on every keystroke. Strip the literal "+1" prefix FIRST: at 5 digits the count test
  // below cannot see the country code, and the 1 would be counted as a national digit.
  const trimmed = (value ?? '').trim();
  const national = trimmed.startsWith('+1') ? trimmed.slice(2) : trimmed;
  const digits = national.replace(/\D/g, '');
  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
}

/**
 * Format as the user types: 555 -> "(555", 5550102 -> "(555) 010-2",
 * 5550102030 -> "(555) 010-2030". Never rejects input, only shapes it —
 * validation is a separate concern so a half-typed number is not an error.
 */
export function formatPhone(value: string): string {
  const d = phoneDigits(value).slice(0, 10);
  if (d.length === 0) return '';
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/**
 * The wire form for every phone Juno sends to odin (and any fleet service): the fleet's
 * canonical key, E.164 digits with NO `+` — "(415) 555-0100" -> "14155550100"
 * (core-commons `standardizeUSPhone`; mercury docs/customer-flow.md Contract 5).
 * odin canonicalizes again on receipt and answers 400 for a non-NANP number.
 * Null when the input is not a complete 10-digit NANP number.
 */
export function phoneForWire(value: string | null | undefined): string | null {
  const d = phoneDigits(value ?? '');
  return d.length === 10 ? `1${d}` : null;
}

/** Empty passes — phone is optional everywhere on this screen. */
export function phoneValidator(control: AbstractControl): ValidationErrors | null {
  const raw = control.value;
  if (!raw) return null;
  return phoneDigits(raw).length === 10 ? null : { phone: true };
}